import type {RefObject} from 'react';

interface ZoomProps {
    /**
     * A string representing the CSS class to be applied to the ZoomIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#6E6E77'
     */
    color1?: string;
    /**
     * A number or string representing the height of the ZoomIcon element.
     *
     * @default 32
     */
    height?: number | string;
    /**
     * A ref object to access the SVG element.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Zoom'
     */
    testId?: string;
    /**
     * A number or string representing the height of the ZoomIcon element.
     *
     * @default 32
     */
    width?: number | string;
}

/**
 * Renders a scalable vector graphic (SVG) representing a "Zoom" icon, commonly used to illustrate magnification or
 * detailed viewing functionality within a user interface. This component supports customizable dimensions, colors,
 * and class names, making it adaptable to various design contexts.
 *
 * The icon is composed of multiple SVG paths and strokes, using rounded caps and joins for smooth edges.
 * It supports dynamic coloring through the `color1` prop and can be easily integrated into both button elements
 * or standalone icon sets. The `ref` prop allows for direct DOM access to the SVG element, useful for animations
 * or further manipulations.
 *
 * @param props           The component props.
 * @param props.className An optional CSS class name applied to the SVG for styling customization.
 * @param props.color1    The primary stroke color of the icon. Defaults to black (`#000`).
 * @param props.height    The height of the SVG in pixels or any valid CSS unit. Defaults to `32`.
 * @param props.ref       A React ref forwarded to the underlying SVG element.
 * @param props.testId    A unique identifier used for testing via `data-cy`. Defaults to `'Zoom'`.
 * @param props.width     The width of the SVG in pixels or any valid CSS unit. Defaults to `32`.
 * @returns A JSX `<svg>` element rendering the zoom icon with configurable visual properties.
 *
 * @example
 * ```tsx
 * <Zoom color1="#ff6600" width={40} height={40} className="zoom-icon" />
 * ```
 */
export const Zoom = ({
    className = '',
    color1 = '#000',
    height = 32,
    ref,
    testId = 'Zoom',
    width = 32
}: ZoomProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        fill="none"
        height={height}
        viewBox="0 0 32 32"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <g clipPath="url(#clip0_1135_8684)">
            <path
                // eslint-disable-next-line @stylistic/max-len
                d="M15.2549 25.9995L12.3812 21.0988C12.1253 20.6488 12.0579 20.1159 12.1935 19.6164C12.3291 19.1168 12.6568 18.6912 13.1051 18.4323C13.5534 18.1735 14.0859 18.1025 14.5863 18.2348C15.0868 18.3671 15.5146 18.692 15.7764 19.1386L17.6073 22.079L17.6073 11.4935C17.6073 10.9736 17.8138 10.475 18.1814 10.1074C18.5491 9.73973 19.0477 9.5332 19.5676 9.5332C20.0875 9.5332 20.5861 9.73973 20.9537 10.1074C21.3213 10.475 21.5278 10.9736 21.5278 11.4935V16.9823C21.5278 16.4624 21.7344 15.9638 22.102 15.5961C22.4696 15.2285 22.9682 15.022 23.4881 15.022C24.008 15.022 24.5066 15.2285 24.8742 15.5961C25.2419 15.9638 25.4484 16.4624 25.4484 16.9823V18.5505C25.4484 18.0306 25.6549 17.532 26.0225 17.1644C26.3902 16.7967 26.8888 16.5902 27.4087 16.5902C27.9286 16.5902 28.4272 16.7967 28.7948 17.1644C29.1624 17.532 29.3689 18.0306 29.3689 18.5505L29.3689 22.079C29.3689 24.4313 28.5848 25.9995 28.5848 25.9995"
                stroke={color1}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.26001"
            />
            <path
                d="M10.5391 3.65723H14.9747V8.09283"
                stroke={color1}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.26001"
            />
            <path
                d="M6.08203 16.9873L1.64643 16.9873L1.64643 12.5517"
                stroke={color1}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.26001"
            />
            <path
                d="M6.63281 11.998L1.64276 16.9881"
                stroke={color1}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.26001"
            />
            <path
                d="M9.98438 8.64648L14.9744 3.65643"
                stroke={color1}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.26001"
            />
        </g>
        <defs>
            <clipPath id="clip0_1135_8684">
                <rect fill="white" height="32" width="32" />
            </clipPath>
        </defs>
    </svg>
);